/**
 * オペレーターマスタ(仕様書 §4.1 / 2026-09-09 スタッフ項目表)。
 * 一覧・登録・編集。複数BB ID・時給/単価の改訂履歴・在籍フィルタ。
 * admin=編集可 / viewer=閲覧のみ。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import MultiSelect, { matchesFilter } from '@core/components/MultiSelect';
import { SearchBox, SortTh, matchKeyword, sortRows, useSort } from '@core/components/tableTools';
import { useToast } from '@core/components/Toast';
import { fmtTs } from '@core/lib/auditLog';
import { formatYen } from '@core/lib/money';
import {
  BUSINESS_CODES,
  BUSINESS_LABELS,
  OPERATOR_STATUSES,
  OPERATOR_STATUS_LABELS,
  PAY_TYPE_CODES,
  PAY_TYPE_LABELS,
  WORK_STYLE_CODES,
  WORK_STYLE_CODE_LABELS,
  WORK_STYLE_LABELS,
  wageLabel,
  type BusinessType,
  type Company,
  type Operator,
  type OperatorStatus,
  type PayType,
  type WorkStyle,
} from '@core/lib/types';
import { fetchOperators } from './operatorRepo';
import { fetchCompanies } from './companyRepo';
import OperatorEditModal from './OperatorEditModal';

type SortKey = 'op_code' | 'name' | 'bb_id' | 'company' | 'business' | 'status' | 'work_style' | 'pay_type' | 'wage' | 'created_at';

const STATUS_BADGE: Record<OperatorStatus, string> = {
  active: 'b-ok',
  retired: 'b-ng',
  leave: 'b-pend',
  hmt: 'b-plan',
  transfer: 'b-plan',
};

const STATUS_OPTIONS = OPERATOR_STATUSES.map((key) => ({ key, label: OPERATOR_STATUS_LABELS[key] }));
// 2026-09-17 MTG のコード(M/A/H/T・H/C・A/B)を名前の前に出す
const WORK_STYLE_OPTIONS = (Object.keys(WORK_STYLE_LABELS) as WorkStyle[]).map((key) => ({ key, label: `${WORK_STYLE_CODES[key]} ${WORK_STYLE_CODE_LABELS[key]}` }));
const PAY_TYPE_OPTIONS = (Object.keys(PAY_TYPE_LABELS) as PayType[]).map((key) => ({ key, label: `${PAY_TYPE_CODES[key]} ${PAY_TYPE_LABELS[key]}` }));
const BUSINESS_OPTIONS = (Object.keys(BUSINESS_LABELS) as BusinessType[]).map((key) => ({ key, label: `${BUSINESS_CODES[key]} ${BUSINESS_LABELS[key]}` }));

const tsMillis = (ts: unknown): number =>
  ts && typeof (ts as { toMillis?: () => number }).toMillis === 'function' ? (ts as { toMillis: () => number }).toMillis() : 0;

const uniq = (values: string[]) => Array.from(new Set(values));

// 一覧の表示文字列(並べ替えにも同じ文字列を使う)
const companyNameOf = (companies: Company[], id: string | null) => (id ? (companies.find((c) => c.id === id)?.name ?? id) : '—');
const companyText = (o: Operator, companies: Company[]) => uniq(o.bb_accounts.map((a) => companyNameOf(companies, a.company_id))).join('、');
const businessText = (o: Operator) => uniq(o.bb_accounts.map((a) => `${BUSINESS_CODES[a.business]} ${BUSINESS_LABELS[a.business]}`)).join('／');
const workStyleText = (o: Operator) => uniq(o.bb_accounts.map((a) => `${WORK_STYLE_CODES[a.work_style ?? o.work_style]} ${WORK_STYLE_CODE_LABELS[a.work_style ?? o.work_style]}`)).join('／');
const payTypeText = (o: Operator) => uniq(o.bb_accounts.map((a) => `${PAY_TYPE_CODES[a.pay_type ?? o.pay_type]} ${PAY_TYPE_LABELS[a.pay_type ?? o.pay_type]}`)).join('／');

export default function OperatorsPage() {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const [operators, setOperators] = useState<Operator[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState<Set<OperatorStatus>>(() => new Set<OperatorStatus>(['active']));
  const [workStyles, setWorkStyles] = useState<Set<WorkStyle>>(() => new Set());
  const [payTypes, setPayTypes] = useState<Set<PayType>>(() => new Set());
  const [businesses, setBusinesses] = useState<Set<BusinessType>>(() => new Set());
  const [companyIds, setCompanyIds] = useState<Set<string>>(() => new Set());
  const [keyword, setKeyword] = useState('');
  const { sort, toggle } = useSort<SortKey>();
  /** 'new'=新規登録 / Operator=編集 */
  const [editing, setEditing] = useState<Operator | 'new' | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [ops, cos] = await Promise.all([fetchOperators(), fetchCompanies()]);
      setOperators(ops);
      setCompanies(cos);
    } catch (e) {
      toast(e instanceof Error ? e.message : '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const filtered = useMemo(() => {
    // BB ID 単位の条件(出勤形態/報酬区分/架電内容/架電会社)は、いずれかのBB IDが一致すれば表示する
    const rows = operators
      .filter((o) => matchesFilter(statuses, o.status))
      .filter((o) => workStyles.size === 0 || o.bb_accounts.some((a) => matchesFilter(workStyles, a.work_style ?? o.work_style)))
      .filter((o) => payTypes.size === 0 || o.bb_accounts.some((a) => matchesFilter(payTypes, a.pay_type ?? o.pay_type)))
      .filter((o) => businesses.size === 0 || o.bb_accounts.some((a) => matchesFilter(businesses, a.business)))
      .filter((o) => companyIds.size === 0 || o.bb_accounts.some((a) => matchesFilter(companyIds, a.company_id)))
      .filter((o) => matchKeyword(keyword, o.name, o.op_code, ...o.bb_accounts.flatMap((a) => [a.bb_id, a.ext])));
    return sortRows(rows, sort, (o, key) => {
      switch (key) {
        case 'op_code':
          return o.op_code;
        case 'name':
          return o.name;
        case 'bb_id':
          return o.bb_accounts[0]?.bb_id ?? null;
        case 'company':
          return companyText(o, companies);
        case 'business':
          return businessText(o);
        case 'status':
          return OPERATOR_STATUSES.indexOf(o.status);
        case 'work_style':
          return workStyleText(o);
        case 'pay_type':
          return payTypeText(o);
        case 'wage':
          return o.bb_accounts.length > 0 ? Math.max(...o.bb_accounts.map((a) => a.current_wage ?? 0)) : null;
        case 'created_at':
          return tsMillis(o.created_at);
      }
    });
  }, [operators, companies, statuses, workStyles, payTypes, businesses, companyIds, keyword, sort]);

  const companyOptions = useMemo(() => companies.map((c) => ({ key: c.id, label: c.name })), [companies]);

  return (
    <div className="screen master-screen" style={{ maxWidth: 1280 }}>
      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          スタッフ一覧
          <span className="hint">{filtered.length}名</span>
          {isAdmin && (
            <button className="btn-line" style={{ marginLeft: 'auto' }} onClick={() => setEditing('new')}>
              ＋新規登録
            </button>
          )}
        </h3>

        <div className="toolbar" style={{ marginBottom: 12 }}>
          <div className="tool-filter">
            <MultiSelect label="在籍" options={STATUS_OPTIONS} value={statuses} onChange={setStatuses} />
            <SearchBox value={keyword} onChange={setKeyword} placeholder="名前・BB ID・内線で検索" />
            <MultiSelect label="出勤形態" options={WORK_STYLE_OPTIONS} value={workStyles} onChange={setWorkStyles} />
            <MultiSelect label="報酬区分" options={PAY_TYPE_OPTIONS} value={payTypes} onChange={setPayTypes} />
            <MultiSelect label="架電内容" options={BUSINESS_OPTIONS} value={businesses} onChange={setBusinesses} />
            <MultiSelect label="架電会社" options={companyOptions} value={companyIds} onChange={setCompanyIds} />
          </div>
        </div>

        {loading ? (
          <div className="empty">読み込み中…</div>
        ) : filtered.length === 0 ? (
          <div className="empty">
            該当するスタッフがいません
            {operators.length === 0 && '(CSVインポートまたは新規登録でデータを投入してください)'}
          </div>
        ) : (
          <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <SortTh label="管理番号" sortKey="op_code" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="名前" sortKey="name" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="BB ID(内線)" sortKey="bb_id" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="架電会社" sortKey="company" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="架電内容" sortKey="business" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="在籍" sortKey="status" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="出勤形態" sortKey="work_style" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="報酬区分" sortKey="pay_type" sort={sort} onSort={toggle} defaultDir="asc" />
                <SortTh label="時給／単価" sortKey="wage" sort={sort} onSort={toggle} className="num" />
                <SortTh label="登録日" sortKey="created_at" sort={sort} onSort={toggle} />
                {isAdmin && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr key={o.id}>
                  <td className="hint" style={{ whiteSpace: 'nowrap' }}>{o.op_code}</td>
                  <td style={{ whiteSpace: 'nowrap' }}><b>{o.name}</b></td>
                  <td>
                    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
                      {o.bb_accounts.map((a) => (
                        <span
                          key={a.bb_id}
                          className={`badge ${a.business === 'kyutoki' ? 'b-plan' : 'b-pend'}`}
                          style={a.active ? undefined : { opacity: 0.45 }}
                          title={`${BUSINESS_LABELS[a.business]}${a.active ? '' : ' / 無効'}`}
                        >
                          {a.bb_id}
                          {a.ext ? `(${a.ext})` : ''}
                        </span>
                      ))}
                    </span>
                  </td>
                  <td>{companyText(o, companies)}</td>
                  <td>{businessText(o)}</td>
                  <td>
                    <span className={`badge ${STATUS_BADGE[o.status]}`}>{OPERATOR_STATUS_LABELS[o.status]}</span>
                  </td>
                  <td>{workStyleText(o)}</td>
                  <td>{payTypeText(o)}</td>
                  <td className="num" style={{ whiteSpace: 'nowrap' }}>
                    {o.bb_accounts.map((a, i) => (
                      <span key={a.bb_id} title={`${a.bb_id} ${wageLabel(a.pay_type ?? o.pay_type)}`}>
                        {i > 0 && '／'}
                        {formatYen(a.current_wage ?? 0)}円
                      </span>
                    ))}
                  </td>
                  <td className="hint">{fmtTs(o.created_at, false)}</td>
                  {isAdmin && (
                    <td>
                      <button className="txn-link" onClick={() => setEditing(o)}>
                        編集
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {editing && (
        <OperatorEditModal
          operator={editing === 'new' ? null : editing}
          operators={operators}
          companies={companies}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void reload();
          }}
        />
      )}
    </div>
  );
}
